import React, { useEffect, useState, Fragment } from "react";
import { QRCodeCanvas } from "qrcode.react";
import "./pay.css";
import { Helmet } from "react-helmet";
import ErrorPage from "../components/error-page";
import { Link, useNavigate } from "react-router-dom";
import SecurityBadge from "../components/secrity-badge";
import pako from "pako";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const corruptLinkError =
  "The link you followed may be broken or may have been removed";
const unknownProfileError =
  "Failed to load. Profile might not exist or is banned.";
const networkError = "Check your network connection and try again";

const decodeSession = (encodedPayment) => {
  if (!encodedPayment) return null;

  // Fix '+' that got turned into spaces
  var session = encodedPayment.replace(/ /g, "+");

  // Ensure URL-decoded form
  session = decodeURIComponent(session);

  // 1. Base64 decode → Uint8Array
  const binaryStr = atob(session);
  const binaryData = new Uint8Array(binaryStr.length);
  for (let i = 0; i < binaryStr.length; i++) {
    binaryData[i] = binaryStr.charCodeAt(i);
  }

  // 2. Gzip decompress
  const decompressed = pako.ungzip(binaryData, { to: "string" });

  // 3. Parse query string into an object
  let parsed = Object.fromEntries(new URLSearchParams(decompressed));

  // 4. Fix JSON-like fields
  try {
    if(parsed.metadata){
    parsed.metadata = JSON.parse(parsed.metadata);
    }
    if(parsed.webhookIds){
    parsed.webhookIds = JSON.parse(parsed.webhookIds);
    }    
  } catch {
    return null;
  }
  console.log(parsed);
  return parsed;
};

const getPayUrl = () => window.location.href;

const Pay = () => {
  const [params, setParams] = useState(null);
  const [name, setName] = useState("?");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  // Decode session param
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const sessionParam = query.get("session");

    if (!sessionParam) {
      setError(corruptLinkError);
      setLoading(false);
      return;
    }

    try {
      const decoded = decodeSession(sessionParam);
      if (!decoded) {
        setError(corruptLinkError);
        setLoading(false);
        return;
      }
      setParams(decoded);
    } catch (err) {
      console.error("Failed to decode session", err);
      setError(corruptLinkError);
      setLoading(false);
    }
  }, []);

  // Fetch full name once params are decoded
  useEffect(() => {
    if (!params) return;

    const { userName, phoneNumber, amount, currency, metadata } = params;

    if (!userName) {
      navigate("/");
      return;
    }

    let storeUserName = null;
    if (metadata) {
      try {
        if (metadata.size !== 0) {
          storeUserName = metadata.storeUserName || null;
        }
      } catch {
        setError(corruptLinkError);
        setLoading(false);
        return;
      }
    }
    const isStore = storeUserName !== null;

    const fetchName = async () => {
      try {
        if (amount !== null && currency === null) {
          setError(unknownProfileError);
          return;
        }

        const queryParams = new URLSearchParams({
          userName: isStore ? storeUserName : userName,
          ...(phoneNumber ? { phoneNumber } : {}),
          isStore,
        });

        const res = await fetch(`${API_BASE}/getFullName?${queryParams}`, {
          headers: { "api-version": "v1" },
          "Content-Type": "application/json",
        });

        if (!res.ok) {
          setError(networkError);
          return;
        }

        const data = await res.json();
        setName(data.fullName || "Unknown");
      } catch (err) {
        console.error(err);
        setError(unknownProfileError);
      } finally {
        setLoading(false);
      }
    };

    fetchName();
  }, [params, navigate]);

  if (loading) {
    return (
      <div className="box">
        <div className="loader"></div>
      </div>
    );
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  const { amount, currency } = params || {};
  const initials = (name || "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();

  const formattedAmount =
    amount && currency
      ? new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: currency === "LBP" ? "LBP" : "USD",
          minimumFractionDigits: 0,
        }).format(Number(amount))
      : null;

  const payUrl = getPayUrl();

  return (
    <div className="box">
      <Helmet>
        <title>Profile | CashOver Pay | Secure & Fast Payments</title>
        <meta
          name="description"
          content="Send money securely and instantly to your friends or businesses with CashOver Pay. Use the app to safely transfer LBP, USD, or USDT to anyone in Lebanon."
        />
        <meta
          property="og:title"
          content="Send Money Securely with CashOver Pay"
        />
        <meta
          property="og:description"
          content="Use CashOver Pay to send funds safely and conveniently. Scan the QR code or follow the link to complete your payment quickly and securely."
        />
        <meta
          property="og:image"
          content={`${process.env.REACT_APP_SITE_URL}/pay.webp`}
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="pay-container">
        {/* Left side */}
        <div
          style={{
            height: "100%",
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <div className="left-side">
            <div className="row">
              <Link to="/" className="link-logo">
                <div className="logo-row">
                  <img alt="CashOver Logo" src="/icon.webp" className="logo" />
                  <div className="logo-column">
                    <span className="logo-text">
                      <Fragment>
                        <span className="logo-text3">CASHOVER</span>
                      </Fragment>
                    </span>
                    <span className="logo-text2">
                      <Fragment>
                        <span className="logo-text4">by overlab.io</span>
                      </Fragment>
                    </span>
                  </div>
                </div>
              </Link>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  width: "100%",
                  gap: 32,
                }}
              >
                <div style={{ flexDirection: "column", flex: 1 }}>
                  <h2 className="name">{name}</h2>
                  {formattedAmount ? (
                    <span className="info">
                      Send {formattedAmount ?? amount} to {name}
                    </span>
                  ) : (
                    <span className="info">
                      Scan the QR code or copy {name}’s CashTag above—choose
                      your amount and send it over!
                    </span>
                  )}
                </div>
                <div className="avatar">{initials}</div>
              </div>
            </div>

            {payUrl && (
              <div className="scan-box">
                <div className="scan-text">Scan to Send Me Money</div>
                <div className="qr-code-box">
                  <QRCodeCanvas
                    value={payUrl}
                    size={250}
                    level={"H"}
                    imageSettings={{
                      excavate: true,
                      src: "/icon.webp",
                      height: 60,
                      width: 60,
                    }}
                  />
                </div>
              </div>
            )}

            <SecurityBadge darkMode={false} />
          </div>
        </div>

        {/* Right side */}
        <div className="right-side">
          <div className="right-side-column">
            <h2 className="right-side-title">CASHOVER PAY</h2>
            <h2 className="right-side-subtitle">Change the way you money</h2>
            <Link to="/" className="learn-more-text">
              Learn more
            </Link>
          </div>
          <img src="/pay.webp" style={{ borderRadius: 24 }} />
        </div>
      </div>
    </div>
  );
};

export default Pay;
